/**
 * "Schedules & runs" — the DOOR an object's designer opens onto scheduling.
 *
 * Two halves, and the distinction between them is the whole point of the tab:
 *
 *   somebody runs it → the object's own Review & Execute / Render, unchanged,
 *                      which now leaves a run record like everything else
 *   nobody runs it   → a Scheduler: a cadence, a named identity, and somewhere
 *                      for the result to go
 *
 * The list of schedulers is DERIVED by reading the schedule files on every
 * load. This object's model stores nothing about them, which is exactly why
 * this tab and Advanced → Schedulers can never disagree.
 */
import { useCallback, useEffect, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { schedulesForObject, type ObjectSchedule } from '@/api/schedules'
import {
  RunsPanel, schedulerColors as C, Chip, Empty, cardStyle, formatRelative,
  formatWhen, labelStyle,
} from '@twasta/page-runtime/shell/jobs'
import { makeSchedulerRuntime } from './runtime'

export default function ObjectSchedulesPanel({
  project, objName, kind,
}: {
  project: string
  objName: string
  /** Shapes the wording only — the machinery is identical. */
  kind: 'process' | 'report' | 'visual' | 'dashboard'
}) {
  const navigate = useNavigate()
  const runtime = useMemo(() => makeSchedulerRuntime(project), [project])
  const [schedules, setSchedules] = useState<ObjectSchedule[] | null>(null)

  const reload = useCallback(async () => {
    try {
      const res = await schedulesForObject(project, objName)
      setSchedules(res.schedules || [])
    } catch {
      setSchedules([])
    }
  }, [project, objName])

  useEffect(() => { void reload() }, [reload])

  const openSchedulers = (name?: string) => {
    const q = name ? `?name=${encodeURIComponent(name)}` : '?pane=edit'
    navigate(`/designers/scheduler/${encodeURIComponent(project)}${q}`)
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 13 }}>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 13 }}>
        <div style={{ ...cardStyle, padding: '12px 14px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8,
            marginBottom: 5 }}>
            <span className="material-icons" style={{ fontSize: 18, color: C.ink2 }}>
              person
            </span>
            <div style={{ fontSize: '0.9225rem', fontWeight: 600 }}>
              Somebody runs it
            </div>
          </div>
          <p style={{ margin: 0, fontSize: '0.8525rem', color: C.ink2 }}>
            {ATTENDED[kind]} That has not moved — it now leaves a run record
            like every other execution, which is what the history below lists.
          </p>
        </div>

        <div style={{ ...cardStyle, padding: '12px 14px',
          borderColor: `${C.accent}55` }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8,
            marginBottom: 5 }}>
            <span className="material-icons" style={{ fontSize: 18, color: C.accent }}>
              schedule
            </span>
            <div style={{ fontSize: '0.9225rem', fontWeight: 600 }}>
              Nobody runs it
            </div>
          </div>
          <p style={{ margin: '0 0 10px', fontSize: '0.8525rem', color: C.ink2 }}>
            A <strong>scheduler</strong> runs it unattended, on a cadence, as a
            named identity, and can carry the result somewhere.
          </p>
          <button type="button" className="btn btn-primary"
            onClick={() => openSchedulers()}
            style={{ fontSize: '0.8425rem', padding: '0.4rem 0.8rem' }}>
            <span className="material-icons" style={{ fontSize: 16 }}>add</span>
            Schedule this {kind}
          </button>
          <div style={{ fontSize: '0.8125rem', color: C.faint, marginTop: 7 }}>
            Opens Advanced → Schedulers.
          </div>
        </div>
      </div>

      <div style={{ ...cardStyle, overflow: 'hidden' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8,
          padding: '10px 14px', borderBottom: `1px solid ${C.line}` }}>
          <div style={{ fontSize: '0.875rem', fontWeight: 600, flex: 1 }}>
            Schedulers that run this {kind}
          </div>
          <Chip tone="muted">{schedules?.length ?? 0}</Chip>
        </div>
        {schedules?.length === 0 && (
          <Empty title="None yet"
            hint={`Nothing runs this ${kind} on a cadence. It can still be run by hand, and every run is listed below.`} />
        )}
        {!!schedules?.length && (
          <table style={{ borderCollapse: 'collapse', width: '100%',
            fontSize: '0.875rem' }}>
            <thead>
              <tr>
                {['Scheduler', 'Cadence', 'Step', 'Next run', 'State'].map((h) => (
                  <th key={h} style={{ textAlign: 'left', ...labelStyle,
                    padding: '7px 14px', borderBottom: `1px solid ${C.line}`,
                    background: C.s2 }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {schedules.map((s) => (
                <tr key={s.name} onClick={() => openSchedulers(s.name)}
                  style={{ cursor: 'pointer' }}>
                  <td style={{ ...cell(true), color: C.accent }}>
                    {s.title || s.name}
                  </td>
                  <td style={cell()}>{cadenceText(s)}</td>
                  <td style={cell()}>Step {s.step_no} of {s.step_count}</td>
                  <td style={cell()}>
                    {s.next_run ? (
                      <>
                        {formatWhen(s.next_run)}
                        <div style={{ fontSize: '0.8125rem', color: C.faint }}>
                          {formatRelative(s.next_run)}
                        </div>
                      </>
                    ) : <span style={{ color: C.faint }}>—</span>}
                  </td>
                  <td style={cell()}>
                    <Chip tone={s.enabled ? 'ok' : 'muted'}>
                      {s.enabled ? 'Enabled' : 'Disabled'}
                    </Chip>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
        <div style={{ padding: '9px 14px', borderTop: `1px solid ${C.line}`,
          fontSize: '0.8125rem', color: C.faint }}>
          Read from the schedule files at load. This {kind}&rsquo;s model stores
          nothing about them, so a scheduler can never disagree with the object
          it runs.
        </div>
      </div>

      <RunsPanel runtime={runtime} objName={objName}
        title={`Recent runs of this ${kind}`} pollMs={5000} />
    </div>
  )
}

const ATTENDED: Record<string, string> = {
  process: 'Review & Execute is where a person keys the criteria, looks at what came back and commits it.',
  report: 'The criteria form and Render are where a person asks for a copy now.',
  visual: 'Opening the visual is where a person looks at the numbers now.',
  dashboard: 'Opening the dashboard is where a person looks at the tiles now.',
}

function cell(strong = false) {
  return {
    padding: '9px 14px', borderBottom: `1px solid ${C.line}`,
    color: strong ? C.ink : C.ink2, fontWeight: strong ? 500 : 400,
    verticalAlign: 'top' as const,
  }
}

function cadenceText(s: ObjectSchedule) {
  const c = s.schedule || {}
  const tz = c.timezone || 'UTC'
  if (c.type === 'interval') {
    return `Every ${c.interval_value ?? 1} ${c.interval_unit || 'hours'}`
  }
  if (c.type === 'daily') return `Daily ${c.time} ${tz}`
  if (c.type === 'weekly') return `Weekly ${c.time} ${tz}`
  if (c.type === 'monthly') {
    return `Monthly ${String(c.day) === 'last' ? 'last day' : `day ${c.day}`} ${c.time} ${tz}`
  }
  return `${c.cron_expression || ''} ${tz}`
}
